import React, { useCallback } from "react"
import useEmblaCarousel, { EmblaViewportRefType } from "embla-carousel-react"
import Autoplay, { AutoplayOptionsType } from "embla-carousel-autoplay"
import { BackIcon, Box, BoxProps, ForwardIcon, IconButton } from "@carrotfertility/carotene-core"
import { EmblaCarouselType, EmblaOptionsType } from "embla-carousel"

const CarouselContext = React.createContext<{
  currentIndex: number
  setCurrentIndex: (currentIndex: number) => void
  api: EmblaCarouselType
  viewportRef: EmblaViewportRefType
}>({
  currentIndex: 0,
  setCurrentIndex: () => {
    /* empty */
  },
  api: null,
  viewportRef: null
})

type CarouselProps = Pick<BoxProps, "children"> & {
  options?: EmblaOptionsType & {
    autoplayDelay?: number
  }
  autoplayOptions?: AutoplayOptionsType & {
    disabled: boolean
  }
}

/**
 * Carousel component provides carousel context and manages carousel state.
 * Wraps carousel content and navigation controls.
 * @param {React.ReactNode} props.children - Carousel content and controls.
 * @param {object} [props.options] - Embla carousel options, plus optional autoplayDelay (ms).
 * @param {object} [props.autoplayOptions] - Embla Autoplay plugin options, plus required `disabled` flag.
 * @returns {JSX.Element}
 * @example
 * <Carousel options={{ loop: true }} autoplayOptions={{ disabled: false, delay: 3000 }}>
 *   <CarouselContent>
 *     <CarouselItem>Item 1</CarouselItem>
 *     <CarouselItem>Item 2</CarouselItem>
 *   </CarouselContent>
 *   <CarouselPrevious />
 *   <CarouselNext />
 * </Carousel>
 */
export function Carousel({ children, options, autoplayOptions }: CarouselProps) {
  const nativeCarouselOptions = options ?? {}
  const { disabled: autoplayDisabled, ...nativeAutoplayOptions } = autoplayOptions ?? { disabled: false }
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, ...nativeCarouselOptions }, [
    !autoplayDisabled && Autoplay({ delay: 5000, ...nativeAutoplayOptions })
  ])
  const [currentIndex, setCurrentIndex] = React.useState(0)

  React.useEffect(() => {
    if (!emblaApi) {
      return
    }

    setCurrentIndex(emblaApi.selectedScrollSnap())

    emblaApi.on("select", () => {
      setCurrentIndex(emblaApi.selectedScrollSnap())
    })
  }, [emblaApi])

  return (
    <CarouselContext.Provider value={{ currentIndex, setCurrentIndex, api: emblaApi, viewportRef: emblaRef }}>
      <Box
        aria-roledescription="carousel"
        sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.tokens.spacing.md })}
      >
        {children}
      </Box>
    </CarouselContext.Provider>
  )
}

/**
 * CarouselItem wraps a single carousel slide/item.
 * Should be used inside <CarouselContent>.
 * @param {React.ReactNode} props.children - Content of the carousel item.
 * @returns {JSX.Element}
 * @example
 * <CarouselItem>Slide Content</CarouselItem>
 */
export function CarouselItem({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        flex: "0 0 100%"
      }}
    >
      {children}
    </Box>
  )
}

/**
 * Hook to access the carousel state and API from context.
 * @returns {object} Carousel context value: { currentIndex, setCurrentIndex, api, viewportRef }
 * @example
 * const { currentIndex, api } = useCarouselState()
 */
export const useCarouselState = () => React.useContext(CarouselContext)

/**
 * CarouselContent wraps the carousel's visible area and children.
 * Should be used inside a <Carousel>.
 * @param {React.ReactNode} props.children - Carousel items to display.
 * @returns {JSX.Element}
 * @example
 * <Carousel>
 *   <CarouselContent>
 *     <CarouselItem>...</CarouselItem>
 *   </CarouselContent>
 * </Carousel>
 */
export function CarouselContent({ children }: { children: React.ReactNode }) {
  const { viewportRef } = useCarouselState()
  return (
    <Box sx={(theme) => ({ overflow: "hidden", borderRadius: theme.tokens.borderRadius.md })} ref={viewportRef}>
      <Box sx={{ display: "flex" }}>{children}</Box>
    </Box>
  )
}

/**
 * CarouselPrevious renders a button to scroll to the previous carousel item.
 * Should be used inside a <Carousel>.
 * @returns {JSX.Element}
 * @example
 * <CarouselPrevious />
 */
export function CarouselPrevious(props: { "aria-label": string }) {
  const { api } = useCarouselState()
  const scrollPrev = useCallback(() => {
    if (api) api.scrollPrev()
  }, [api])

  return (
    <IconButton
      aria-label={props["aria-label"]}
      sx={(theme) => ({
        bgcolor: theme.palette.primary.light,
        color: theme.palette.primary.contrastText
      })}
      onClick={scrollPrev}
    >
      <BackIcon />
    </IconButton>
  )
}

/**
 * CarouselNext renders a button to scroll to the next carousel item.
 * Should be used inside a <Carousel>.
 * @returns {JSX.Element}
 * @example
 * <CarouselNext />
 */
export function CarouselNext(props: { "aria-label": string }) {
  const { api } = useCarouselState()
  const scrollNext = useCallback(() => {
    if (api) api.scrollNext()
  }, [api])

  return (
    <IconButton
      aria-label={props["aria-label"]}
      sx={(theme) => ({
        bgcolor: theme.palette.primary.light,
        color: theme.palette.primary.contrastText
      })}
      onClick={scrollNext}
    >
      <ForwardIcon />
    </IconButton>
  )
}
